import { useEffect, useState } from "react";

import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";
import { isValidNumericParam } from "@/shared/utils/common.utils";
import { notification } from "antd";
import { AssessmentInviteStatus } from "../scenes/AssessmentInvites/invites.constants";
import { useNavigate } from "react-router-dom";
import { LanguagesIsoEnum, ResolvedLocalizedContent } from "@/modules/assessments/types/test.interface";
import { DEFAULT_LOCALE } from "@/i18n";

export interface IDevelopmentSequence {
  rank: number;
  sectionType: string;
  scaleName: ResolvedLocalizedContent;
  scoreType: "top_strength" | "developing" | "opportunity";
  sectionName: string;
  scoreTypeLabel: string;
  content: {
    // BE resolves each field to the requested locale (with en-US fallback) and returns the served
    // locale alongside the value.
    description: ResolvedLocalizedContent;
    tips: ResolvedLocalizedContent[];
  };
}

export function useGetInviteScaleDevelopmentHook({
  inviteId,
  shouldLoadScaleDevelopment,
  inviteStatus,
  locale = DEFAULT_LOCALE,
}: {
  inviteId: number;
  shouldLoadScaleDevelopment: boolean;
  inviteStatus?: AssessmentInviteStatus;
  locale?: LanguagesIsoEnum;
}) {
  const navigate = useNavigate();
  const [developmentSequences, setDevelopmentSequences] = useState<IDevelopmentSequence[]>([]);
  // Locales the template is fully translated into, or undefined when coverage is unknown (not yet
  // fetched or the request failed). Undefined means "don't gate".
  const [availableLocales, setAvailableLocales] = useState<LanguagesIsoEnum[] | undefined>(undefined);
  // True only until the first content response resolves; language-switch re-fetches don't flip it back,
  // so callers gate the initial load without re-gating on every switch.
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const shouldLoad = shouldLoadScaleDevelopment && inviteStatus === AssessmentInviteStatus.Done;

  // Content: re-fetched whenever the requested locale changes. A content failure blanks the tab and
  // notifies; the BE falls back to en-US per field so a non-English request never half-blanks.
  useEffect(() => {
    if (!isValidNumericParam(inviteId)) {
      notification.error({ message: "The assessment invite is not valid." });
      navigate(-1);
      return;
    }

    if (!shouldLoad) {
      setDevelopmentSequences([]);
      setIsInitialLoading(false);
      return;
    }

    // Guards against a slower earlier response (e.g. after a locale change) overwriting a newer one.
    let cancelled = false;

    const fetchContent = async () => {
      try {
        const data: IDevelopmentSequence[] = await InvitesHttpService.getInviteScaleDevelopment(
          Number(inviteId),
          locale,
        );
        if (!cancelled) setDevelopmentSequences(data);
      } catch {
        if (cancelled) return;
        notification.error({
          message: "Error fetching develop report. Please refresh and try again or contact support for help.",
        });
        setDevelopmentSequences([]);
        // Reset coverage to "unknown" so a transient failure blanks the tab rather than showing the
        // misleading "not available in this language" empty state.
        setAvailableLocales(undefined);
      } finally {
        if (!cancelled) setIsInitialLoading(false);
      }
    };

    fetchContent();

    return () => {
      cancelled = true;
    };
  }, [inviteId, shouldLoad, locale, navigate]);

  // Coverage list: fetched once per invite, independent of the selected locale, so the language
  // control can offer options while still on the default English. Only feeds the empty-state gate, so
  // a failure degrades to "unknown coverage" (don't gate) rather than blanking the tab.
  useEffect(() => {
    if (!isValidNumericParam(inviteId) || !shouldLoad) {
      setAvailableLocales(undefined);
      return;
    }

    let cancelled = false;

    const fetchLanguages = async () => {
      try {
        const { languages } = await InvitesHttpService.getInviteScaleDevelopmentLanguages(Number(inviteId));
        if (!cancelled) setAvailableLocales(languages);
      } catch {
        if (!cancelled) setAvailableLocales(undefined);
      }
    };

    fetchLanguages();

    return () => {
      cancelled = true;
    };
  }, [inviteId, shouldLoad]);

  return { developmentSequences, availableLocales, isInitialLoading };
}
